import Box from 'assets/components/box/Box';
import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import ThemeButtonFloating from 'assets/components/button/themes/Theme.Button.Floating';
import DataMap from 'assets/components/dataMap/DataMap';
import FormatValue from 'assets/components/formatValue/FormatValue';
import Icon from 'assets/components/icon/Icon';
import Page from 'assets/components/page/Page';
import ThemePageError from 'assets/components/page/themes/Theme.Page.Error';
import ThemePageList from 'assets/components/page/themes/Theme.Page.List';
import Pagination from 'assets/components/pagination/Pagination';
import ThemePaginationRounded from 'assets/components/pagination/themes/Theme.Pagination.Rounded';
import ThemeTableSimple from 'assets/components/table/themes/Theme.Table.Simple';
import ThemeTagRounded from 'assets/components/tag/themes/Theme.Tag.Rounded';
import { getLocales } from 'assets/locales/Locale';
import Theme from 'assets/themes/Theme.Common';
import ThemeWrapperStack from 'assets/themes/wrappers/Theme.Wrapper.Stack';
import { defaultDateFormat, localizeDate } from 'assets/utils/data/Date';
import { merge } from 'lodash';
import AccountReceivableStatuses from 'models/enums/AccountReceivableStatuses';
import React from 'react';
import { isDesktop, isMobile } from 'react-device-detect';
import { AccountReceivableAction, useAccountReceivableProvider } from '../Finance.AccountReceivable.Hooks';
import FinanceAccountReceivableFilters from './Finance.AccountReceivable.Filters';

export default function FinanceAccountReceivableList() {
  const { lang, translate } = getLocales();
  const { listApi, pageRouter, access, getStatusTag } = useAccountReceivableProvider();

  return !listApi.payload && !listApi.isExecuting && listApi.apiCounter > 0 ? (
    <Page
      class={ThemePageError}
      sidebar={<Icon class="fas-money-check-alt" />}
      header={lang.accountReceivables}
      render={lang.noInfo}
    />
  ) : (
    <Page
      class={ThemePageList}
      isLoading={listApi.isExecuting}
      header={
        <>
          <h2 style={{ marginRight: 'auto' }} children={lang.accountReceivables} />
          <Box class={ThemeWrapperStack}>
            {isMobile && <FinanceAccountReceivableFilters />}
            <Button
              class={isMobile ? ThemeButtonFloating : ThemeButtonCircle}
              media={<Icon class="fas-file-import" />}
              render={!isMobile && lang.import}
              onClick={pageRouter.updateParams}
              disabled={!access.canImport}
              active
              data={{ action: AccountReceivableAction.IMPORT }}
            />
            <Button
              class={isMobile ? ThemeButtonFloating : ThemeButtonCircle}
              active
              media={<Icon class="fas-plus" />}
              render={!isMobile && lang.addNew}
              onClick={pageRouter.updateParams}
              disabled={!access.canCreate}
              data={{ action: AccountReceivableAction.CREATE }}
            />
          </Box>
        </>
      }
      subHeader={isDesktop && <FinanceAccountReceivableFilters />}
      render={
        <table className={ThemeTableSimple}>
          <thead>
            <tr>
              <th>{lang.dealer}</th>
              <th>{lang.amount}</th>
              <th>{lang.statement}</th>
              <th>{lang.datePaymentReceived}</th>
              <th>{lang.paymentMethod}</th>
              <th>{lang.evidenceSource}</th>
              <th>{lang.referenceNumber}</th>
              <th>{lang.status}</th>
              <th data-actions>{lang.actions}</th>
            </tr>
          </thead>
          <tbody>
            <DataMap
              data={listApi.payload?.data}
              render={({ data: ar }) => {
                const statusTag = getStatusTag(ar.statusCode);
                const isEditDisabled =
                  !!ar.statementId ||
                  !access.canEdit ||
                  ar.statusCode === AccountReceivableStatuses.RECEIVED ||
                  ar.statusCode === AccountReceivableStatuses.PROCESSED;

                return (
                  <React.Fragment key={ar.id}>
                    <tr style={{ borderTop: `2px solid ${Theme.Color.shadow}` }}>
                      <td>
                        <b>{ar.displayInfo?.title}</b>
                      </td>
                      <td>
                        <b>
                          <FormatValue value={`${(ar.amount || 0).toFixed(2)}$`} />
                        </b>
                      </td>
                      <td>{ar.displayInfo?.subtitle || 'N/A'}</td>
                      <td>{localizeDate(ar.paymentReceivedAtUtc, defaultDateFormat)}</td>
                      <td>{translate(ar.paymentMethodCode) || 'N/A'}</td>
                      <td>{translate(ar.evidenceSource) || 'N/A'}</td>
                      <td>{ar.externalPaymentReference || ar.internalPaymentReference || 'N/A'}</td>
                      <td>
                        <Box
                          class={ThemeTagRounded}
                          style={{ backgroundColor: statusTag.backgroundColor, color: statusTag.color }}
                        >
                          {statusTag.label}
                        </Box>
                      </td>
                      <td>
                        <Box class={ThemeWrapperStack}>
                          <Button
                            onClick={pageRouter.updateParams}
                            data={{ id: ar.id.toString(), action: AccountReceivableAction.INFO }}
                            class={ThemeButtonCircle}
                            htmlElementProps={{ title: lang.info }}
                            media={<Icon class="fas-info-circle" />}
                            active
                          />
                          <Button
                            onClick={pageRouter.updateParams}
                            data={{ id: ar.id.toString(), action: AccountReceivableAction.EDIT }}
                            disabled={isEditDisabled}
                            class={ThemeButtonCircle}
                            htmlElementProps={{ title: lang.edit }}
                            media={<Icon class="fas-edit" />}
                            active
                          />
                        </Box>
                      </td>
                    </tr>
                  </React.Fragment>
                );
              }}
            />
          </tbody>
        </table>
      }
      footer={
        <Pagination
          class={ThemePaginationRounded}
          count={listApi.payload?.count}
          offset={listApi.body?.offset}
          limit={listApi.body?.limit}
          onLimitChange={(limit) =>
            pageRouter.updateQuery(
              merge(pageRouter.query, {
                list: {
                  limit,
                  offset: 0,
                },
              })
            )
          }
          onOffsetChange={(offset) =>
            pageRouter.updateQuery(
              merge(pageRouter.query, {
                list: {
                  offset,
                },
              })
            )
          }
        />
      }
    />
  );
}
